import { useState } from "react";
import { useQuery } from "react-query";
import { paywallSubscriptionsApi } from "@/react/api/paywallSubscriptionsApi";
import type { CommunityMemberSubscription } from "@/react/types/CommunityMemberSubscription";

const RESULTS_PER_PAGE = 10;

export const paywallSubscriptionQueryString = "paywall-subscriptions";

type FIELDS_AVAILABLE_TO_ORDER = "created_at";

export interface CommunityMemberSubscriptionResponse {
  count: number;
  records: CommunityMemberSubscription[];
}

interface UseAdminPaywallSubscriptionProp {
  enabled?: boolean;
  filters?: FiltersProp;
  orderParam?: Record<FIELDS_AVAILABLE_TO_ORDER, "desc" | "asc">;
  page: string | number;
  perPage?: number;
  searchKeyParams?: string;
  setPage: (value: any) => void;
}

interface FiltersProp {
  community_member_id?: string;
  community_member_public_uid?: string;
  paywall_ids?: string;
  processor_id?: string;
}

export const useAdminPaywallSubscription = ({
  orderParam = { created_at: "desc" },
  perPage = RESULTS_PER_PAGE,
  searchKeyParams = "",
  filters = {},
  enabled = true,
  page,
  setPage,
}: UseAdminPaywallSubscriptionProp): {
  hasFilters: boolean;
  subscriptions: CommunityMemberSubscription[];
  totalSubscriptions: number;
  refetchSubscriptions: () => Promise<any>;
  removeSubscriptionsFromCache: () => void;
  perPage: number;
  page: string | number;
  order: Record<FIELDS_AVAILABLE_TO_ORDER, "desc" | "asc">;
  setOrder: (value: any) => void;
  setPage: (value: any) => void;
  isLoadingSubscriptions: boolean;
} => {
  const [order, setOrder] = useState(orderParam);

  const httpParams = {
    filters,
    order,
    page,
    perPage,
    searchKey: searchKeyParams,
  };

  const fetchPaywalls = paywallSubscriptionsApi.index(httpParams);

  const {
    data,
    refetch: refetchSubscriptions,
    isLoading: isLoadingSubscriptions,
    remove: removeSubscriptionsFromCache,
  } = useQuery<CommunityMemberSubscriptionResponse>({
    queryKey: [
      paywallSubscriptionQueryString,
      {
        filters,
        order,
        page,
        perPage,
      },
      searchKeyParams,
    ],
    queryFn: fetchPaywalls,
    enabled,
  });

  const updateOrder = (value: any) => {
    setOrder(value);
    setPage(1);
  };

  return {
    hasFilters: Object.keys(filters).length > 0,
    subscriptions: data?.records ?? [],
    totalSubscriptions: data?.count ?? 0,
    refetchSubscriptions,
    removeSubscriptionsFromCache,
    perPage,
    page,
    order,
    setOrder: updateOrder,
    setPage,
    isLoadingSubscriptions,
  };
};
